import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, ChevronDown } from "lucide-react";

const VOLUMES = ["<100", "100–300", "300–1,000", "1,000+"];
const GEOS = ["North America", "EMEA", "APAC", "LATAM", "Global"];
const SERVICES = [
  "Content Syndication",
  "MQL / HQL Leads",
  "BANT / CCL Leads",
  "Account-Based Marketing",
  "Webinar Registrations",
  "Data Cleansing & Enrichment",
];

const inputCls =
  "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-charcoal placeholder:text-slate-400 transition-all duration-200";

const Field = ({ label, children, span = false }) => (
  <label className={`block ${span ? "sm:col-span-2" : ""}`}>
    <span className="mb-1.5 block font-mono2 text-[10px] tracking-[0.18em] text-slate-500">{label}</span>
    {children}
  </label>
);

export const ProposalForm = ({ dark = false, testIdPrefix = "proposal" }) => {
  const [volume, setVolume] = useState("100–300");
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "", company: "", jobTitle: "",
    website: "", geography: "North America", service: "Content Syndication", notes: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/proposals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, volume }),
      });
      if (!res.ok) throw new Error("submit failed");
      toast.success("Proposal request received", {
        description: "A dedicated strategist will deliver your custom TAM count and CPL pricing within 24 business hours.",
      });
      setForm({ firstName: "", lastName: "", email: "", phone: "", company: "", jobTitle: "", website: "", geography: "North America", service: "Content Syndication", notes: "" });
    } catch {
      toast.error("Submission failed", {
        description: "Please try again, or email us directly at sales@vyacomm-media.com.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={`${testIdPrefix}-form`} className="grid gap-4 sm:grid-cols-2">
      <Field label="FIRST NAME *">
        <input required data-testid={`${testIdPrefix}-first-name`} className={inputCls} placeholder="Jordan" value={form.firstName} onChange={set("firstName")} />
      </Field>
      <Field label="LAST NAME *">
        <input required data-testid={`${testIdPrefix}-last-name`} className={inputCls} placeholder="Reyes" value={form.lastName} onChange={set("lastName")} />
      </Field>
      <Field label="CORPORATE EMAIL *">
        <input required type="email" data-testid={`${testIdPrefix}-email`} className={inputCls} placeholder="j.reyes@company.com" value={form.email} onChange={set("email")} />
      </Field>
      <Field label="PHONE NUMBER *">
        <input required type="tel" data-testid={`${testIdPrefix}-phone`} className={inputCls} placeholder="+1 (555) 000-0000" value={form.phone} onChange={set("phone")} />
      </Field>
      <Field label="COMPANY NAME *">
        <input required data-testid={`${testIdPrefix}-company`} className={inputCls} placeholder="Acme Systems Inc." value={form.company} onChange={set("company")} />
      </Field>
      <Field label="JOB TITLE *">
        <input required data-testid={`${testIdPrefix}-job-title`} className={inputCls} placeholder="VP Demand Generation" value={form.jobTitle} onChange={set("jobTitle")} />
      </Field>
      <Field label="COMPANY WEBSITE *" span>
        <input required data-testid={`${testIdPrefix}-website`} className={inputCls} placeholder="https://company.com" value={form.website} onChange={set("website")} />
      </Field>
      <Field label="TARGET GEOGRAPHY">
        <span className="relative block">
          <select data-testid={`${testIdPrefix}-geography`} className={`${inputCls} appearance-none`} value={form.geography} onChange={set("geography")}>
            {GEOS.map((g) => <option key={g}>{g}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        </span>
      </Field>
      <Field label="PRIMARY SOLUTION NEEDED">
        <span className="relative block">
          <select data-testid={`${testIdPrefix}-service`} className={`${inputCls} appearance-none`} value={form.service} onChange={set("service")}>
            {SERVICES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        </span>
      </Field>
      <div className="sm:col-span-2">
        <span className="mb-1.5 block font-mono2 text-[10px] tracking-[0.18em] text-slate-500">ESTIMATED MONTHLY LEAD ALLOCATION</span>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid={`${testIdPrefix}-volume-group`}>
          {VOLUMES.map((v) => (
            <button
              type="button"
              key={v}
              data-testid={`${testIdPrefix}-volume-${v.replace(/[^0-9a-z<>]+/gi, "-")}`}
              onClick={() => setVolume(v)}
              className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-all duration-200 ${
                volume === v
                  ? "border-cobalt bg-cobalt text-white shadow-[0_8px_20px_-8px_rgba(37,99,235,0.7)]"
                  : "border-line bg-white text-slate-600 hover:border-cobalt/50"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
      <Field label="CAMPAIGN GOALS & TARGET CRITERIA" span>
        <textarea
          rows={4}
          data-testid={`${testIdPrefix}-notes`}
          className={`${inputCls} resize-none`}
          placeholder="Target titles, industries, regions, timeline, and anything else we should know…"
          value={form.notes}
          onChange={set("notes")}
        />
      </Field>
      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={sending}
          data-testid={`${testIdPrefix}-submit-btn`}
          className="group flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-8 py-4 text-sm font-bold text-white shadow-[0_16px_40px_-12px_rgba(37,99,235,0.7)] transition-all duration-300 hover:scale-[1.01] hover:bg-[#1d4fd7] active:scale-[0.98] disabled:opacity-70"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {sending ? "Generating…" : "Generate Custom Proposal"}
          {!sending && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
        </button>
        <p className="mt-3 text-center text-[11px] text-slate-400">
          Zero obligation · Feasibility count within 24 business hours · 100% opt-in verified data only
        </p>
      </div>
    </form>
  );
};
